import type { ModuleSynergyDefinition } from "../data/moduleSynergyDefinitions.js";

/**
 * **시너지 기여 모듈 식별** (순수) — 발동 중인 시너지(matched)의 requiredModuleIds를 실제로 만족한
 * 인접 경로 구간의 instanceId 집합을 반환한다. ModuleGridView가 이 집합의 셀을 "synergy" 색으로
 * 별도 하이라이트한다(유저가 어떤 붙어 있는 모듈 조합이 시너지를 이루는지 시각 인지).
 *
 * fused 모듈도 기여 가능하도록 인스턴스당 `definitionIds`(융합이면 컴포넌트 ids, 아니면 [definitionId])를
 * 받아 required와 교집합이 있으면 기여로 판정한다(resolveModuleSynergies가 sourceModuleIds로 매칭하는 것과 정합).
 */
export function resolveSynergyContributors(
  matched: readonly ModuleSynergyDefinition[],
  activeModules: readonly {
    instanceId: string;
    definitionIds: readonly string[];
    cells?: readonly { readonly x: number; readonly y: number }[];
  }[],
): Set<string> {
  if (matched.length === 0) return new Set();
  const out = new Set<string>();
  for (const s of matched) {
    for (const instanceId of resolveSingleSynergyContributors(s, activeModules)) out.add(instanceId);
  }
  return out;
}

function resolveSingleSynergyContributors(
  synergy: ModuleSynergyDefinition,
  activeModules: readonly {
    instanceId: string;
    definitionIds: readonly string[];
    cells?: readonly { readonly x: number; readonly y: number }[];
  }[],
): Set<string> {
  const required = new Set(synergy.requiredModuleIds);
  for (let start = 0; start < activeModules.length; start++) {
    const seen = new Set<string>();
    const ids = new Set<string>();
    for (let end = start; end < activeModules.length; end++) {
      const module = activeModules[end]!;
      if (end > start && !modulesTouch(activeModules[end - 1]!, module)) break;
      const matchingIds = module.definitionIds.filter((id) => required.has(id));
      if (matchingIds.length === 0) break;
      for (const id of matchingIds) seen.add(id);
      ids.add(module.instanceId);
      if (synergy.requiredModuleIds.every((id) => seen.has(id))) return ids;
    }
  }
  return new Set();
}

function modulesTouch(
  a: { readonly cells?: readonly { readonly x: number; readonly y: number }[] },
  b: { readonly cells?: readonly { readonly x: number; readonly y: number }[] },
): boolean {
  if (!a.cells || !b.cells) return true;
  for (const ca of a.cells) {
    for (const cb of b.cells) {
      if (Math.abs(ca.x - cb.x) + Math.abs(ca.y - cb.y) === 1) return true;
    }
  }
  return false;
}
